import React, { useState } from 'react';
import { Stack, styled } from '@mui/material';
import { useNavigate } from 'react-router';
import { ContainerLayout } from 'components/index';
import { ArticleView } from 'components/ArticleView/ArticleView';
import { BackButton } from 'components/BackButton/BackButton';
import { SearchBar } from 'components/SearchBar/SearchBar';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { toLibraryArticleFavoriteRequest } from 'features/resources/utils/libraryArticleFavoriteUtils';
import { useLibraryArticleFavorites, useResources } from 'features/resources/hooks';
import { useDebouncedValue } from 'hooks/useDebouncedvalue';
import { RESOURCE_LAYOUT_GRID_COLUMNS } from 'themes/constants';
import { Alert } from 'components/Alert/Alert';
import { TransformedLibraryArticle } from 'features/resources/types';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { PendoEventType } from 'features/pendo/types';
import { ResourceGridContainer } from './ResourceSection';
import { ContentSearchResults } from './ContentSearchResults';

const SEARCH_DEBOUNCE_MS = 500;

const PageColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
}));

const SearchHeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 1.75),
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
  backgroundColor: theme.palette.background.paper,
}));

const ScrollArea = styled('div')(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
}));

const ResultsGrid2 = styled(ResourceGridContainer)(({ theme }) => ({
  paddingBlock: theme.spacing(2),
  paddingBottom: theme.spacing(18),
}));

/** Mobile-only full page reached via the search icon on the Library page (WFMOB-16222/WFMOB-15503). */
export const LibrarySearchPage = () => {
  const navigate = useNavigate();
  const { translation } = useTranslationContext();
  const [searchTerm, setSearchTerm] = useState('');
  const { trackEvent } = usePendo();
  const { debouncedValue: debouncedSearchTerm, flush: flushSearchDebounce } = useDebouncedValue(
    searchTerm,
    SEARCH_DEBOUNCE_MS,
  );
  const [isFavoriteToastPresented, setIsFavoriteToastPresented] = useState(false);
  const { activeArticle, handleArticleClick, handleModalClose } = useResources();
  const { isFavorited, toggleFavorite } = useLibraryArticleFavorites();
  const goBack = () => navigate(-1);

  const handleSearchSubmit = (keyword: string) => {
    const searchKeyword = keyword.trim();
    if (searchKeyword) {
      trackEvent(PendoEventType.SEARCH_KEYWORD_ARTICLE, {
        eventType: PendoEventType.SEARCH_KEYWORD_ARTICLE,
        searchKeyword,
      });
    }

    flushSearchDebounce();
  };

  const handleToggleFavorite = async (article: TransformedLibraryArticle) => {
    try {
      const result = await toggleFavorite(toLibraryArticleFavoriteRequest(article));
      if (result === 'added') {
        trackEvent(PendoEventType.FAVORITE_ARTICLE, {
          eventType: PendoEventType.FAVORITE_ARTICLE,
          articleId: `${article.id}`,
        });
        setIsFavoriteToastPresented(true);
      } else {
        trackEvent(PendoEventType.REMOVED_FAVORITE_ARTICLE, {
          eventType: PendoEventType.REMOVED_FAVORITE_ARTICLE,
          articleId: `${article.id}`,
        });
      }
    } catch {
      // Favorite failed; no toast shown.
    }
  };

  return (
    <ContainerLayout>
      <PageColumn>
        <SearchHeaderRow>
          <BackButton onClick={goBack} />
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onSubmit={handleSearchSubmit}
            placeholder={translation('search_bar_copy')}
            autoFocus
          />
        </SearchHeaderRow>
        <ScrollArea>
          <ResultsGrid2 container spacing={2} columns={RESOURCE_LAYOUT_GRID_COLUMNS}>
            <ContentSearchResults
              searchTerm={debouncedSearchTerm.trim()}
              onArticleClick={handleArticleClick}
              onToggleFavorite={handleToggleFavorite}
              isArticleFavorited={(article) => isFavorited(article.contentUrl)}
            />
          </ResultsGrid2>
        </ScrollArea>
      </PageColumn>

      {activeArticle && (
        <ArticleView
          showHeader
          showFavorite
          title={activeArticle.title}
          open
          url={activeArticle.contentUrl}
          isFavorited={isFavorited(activeArticle.contentUrl)}
          onToggleFavorite={() => handleToggleFavorite(activeArticle)}
          onClose={handleModalClose}
          overrideBackButtonVisible
          onClickBack={handleModalClose}
        />
      )}
      <Alert
        isPresented={isFavoriteToastPresented}
        handleDismiss={() => setIsFavoriteToastPresented(false)}
        message={translation('content_favorites_add')}
        presentationDuration={5000}
        variant="filled"
        icon={false}
        snackbarSx={{ marginBottom: 3.5 }}
        sx={{ backgroundColor: 'brandShade2.main' }}
      />
    </ContainerLayout>
  );
};
